<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { getOrders } from '../../services/orderApi'
import ALoader from '../common/ALoader.vue'
import { OrderInterface, OrderType } from '../../models/OrderModel'
import OrderItem from './OrderItem.vue'

const props = defineProps({
  type: OrderType,
})

const isLoading = ref<boolean>(true)
const orders = ref<OrderInterface[]>([])

const searchInput = ref<string>('')
const searchQuery = ref<string>('')

const captureEl = ref<HTMLInputElement | null>(null)
const searchEl = ref<HTMLInputElement | null>(null)
const captureValue = ref<string>('')

let searchDebounceTimer: number | null = null
let commitTimer: number | null = null
let focusGuardTimer: number | null = null

const COMMIT_AFTER_MS = 120
const FOCUS_GUARD_MS = 500

const debugEnabled = !!(import.meta.env.VITE_DEBUG as number)

const router = useRouter()
const enterQueued = ref<boolean>(false)

const canCaptureFocus = computed(() => {
  return !isLoading.value
})

function isAllowedFocusedElement(el: Element | null): boolean {
  return el === captureEl.value || el === searchEl.value
}

function ensureScannerFocus() {
  if (!canCaptureFocus.value) return

  const active = document.activeElement
  if (isAllowedFocusedElement(active)) return

  void focusCapture()
}

function startFocusGuard() {
  stopFocusGuard()

  focusGuardTimer = window.setInterval(() => {
    ensureScannerFocus()
  }, FOCUS_GUARD_MS)
}

function stopFocusGuard() {
  if (focusGuardTimer) {
    window.clearInterval(focusGuardTimer)
    focusGuardTimer = null
  }
}

watch(
  () => canCaptureFocus.value,
  (canFocus) => {
    if (canFocus) {
      void focusCapture()
      startFocusGuard()
      return
    }

    stopFocusGuard()
  },
  { immediate: true }
)

watch(searchInput, (value) => {
  if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer)

  searchDebounceTimer = window.setTimeout(() => {
    searchQuery.value = value.trim()
  }, 250)
})

function orderMatchesQuery(order: OrderInterface, q: string): boolean {
  const query = q.toLowerCase()
  if (!query) return true

  return [order.id, order.order_number]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase())
    .some((value) => value.includes(query))
}

const filteredOrders = computed(() => {
  const q = searchQuery.value
  if (!q) return orders.value

  return orders.value.filter((order) => orderMatchesQuery(order, q))
})

function openOrder(order: OrderInterface) {
  router.push({
    name: 'order',
    params: { id: order.id },
    query: { type: order.type },
  })
}

watch(searchInput, (value, oldValue) => {
  if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer)

  searchDebounceTimer = window.setTimeout(() => {
    searchQuery.value = value.trim()

    nextTick(() => {
      if (value.length - oldValue.length > 1) {
        enterQueued.value = true
      }

      if (!enterQueued.value) return

      if (filteredOrders.value.length !== 1) {
        enterQueued.value = false
        return
      }

      openOrder(filteredOrders.value[0])
      enterQueued.value = false
    })
  }, 250)
})

watch(enterQueued, () => {
  if (!enterQueued.value) return
  if (filteredOrders.value.length !== 1) return

  openOrder(filteredOrders.value[0])
  enterQueued.value = false
})

function onSearchKeyUp(e: KeyboardEvent) {
  if (e.key !== 'Enter') return
  enterQueued.value = true
}

onMounted(async () => {
  let orderType: string | undefined = props.type

  if (orderType === OrderType.all) {
    orderType = undefined
  }

  getOrders(orderType)
    .then((parsedOrders) => {
      orders.value = parsedOrders
    })
    .finally(() => {
      isLoading.value = false
      void focusCapture()
    })
})

function scheduleCommit() {
  if (commitTimer) window.clearTimeout(commitTimer)
  commitTimer = window.setTimeout(() => commitScan(), COMMIT_AFTER_MS)
}

async function commitScan() {
  const value = (captureValue.value ?? '').trim()
  if (!value) return

  enterQueued.value = true
  await nextTick()

  searchInput.value = value

  captureValue.value = ''
  void focusCapture()
}

async function focusCapture() {
  await nextTick()
  if (!canCaptureFocus.value) return

  const el = captureEl.value
  if (!el) return

  try {
    el.focus({ preventScroll: true } as any)
  } catch {
    el.focus()
  }
}

function decodeKey(e: KeyboardEvent): string | null {
  if (typeof e.key === 'string' && e.key) {
    if (e.key === 'Enter') return 'ENTER'
    if (e.key === 'Tab') return 'TAB'
    if (e.key.length === 1) return e.key
    return null
  }

  const anyE = e as any
  const code: number | undefined = typeof anyE.keyCode === 'number' ? anyE.keyCode : anyE.which

  if (typeof code !== 'number') return null

  if (code === 13) return 'ENTER'
  if (code === 9) return 'TAB'

  if (code >= 48 && code <= 57) return String.fromCharCode(code)
  if (code >= 96 && code <= 105) return String(code - 96)

  if (code >= 65 && code <= 90) {
    const ch = String.fromCharCode(code)
    return e.shiftKey ? ch : ch.toLowerCase()
  }

  if (code === 189 || code === 109) return e.shiftKey ? '_' : '-'
  if (code === 190 || code === 110) return '.'
  if (code === 191 || code === 111) return '/'

  return null
}

function onCaptureKeydown(e: KeyboardEvent) {
  if (!canCaptureFocus.value) return

  const decoded = decodeKey(e)
  if (!decoded) return

  if (decoded === 'ENTER' || decoded === 'TAB') {
    e.preventDefault()
    e.stopPropagation()
    commitScan()
    return
  }

  captureValue.value += decoded
  scheduleCommit()
}

function onCaptureBlur() {
  window.setTimeout(() => {
    if (!canCaptureFocus.value) return

    const active = document.activeElement
    if (active === searchEl.value) return
    if (active === captureEl.value) return

    void focusCapture()
  }, 0)
}

function onSearchBlur() {
  window.setTimeout(() => void focusCapture(), 0)
}

onBeforeUnmount(() => {
  if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer)
  if (commitTimer) window.clearTimeout(commitTimer)
  stopFocusGuard()
})
</script>

<template>
  <ALoader v-if="isLoading" />

  <div v-else>
    <div v-if="props.type === OrderType.order">
      <RouterLink
        class="btn btn-primary py-12 mb-12 d-flex justify-content-center"
        :to="{ name: 'new-sale' }"
      >
        {{ $t('new-order') }}
      </RouterLink>
    </div>
    <div class="mb-24">
      <input
        ref="captureEl"
        v-model="captureValue"
        class="form-control search"
        :class="{ 'scanner-capture': !debugEnabled }"
        type="text"
        autocomplete="off"
        spellcheck="false"
        inputmode="none"
        enterkeyhint="done"
        aria-hidden="true"
        tabindex="-1"
        @keydown="onCaptureKeydown"
        @blur="onCaptureBlur"
      />

      <input
        ref="searchEl"
        v-model="searchInput"
        type="search"
        class="form-control search"
        :placeholder="$t('search')"
        autocomplete="off"
        spellcheck="false"
        @blur="onSearchBlur"
        @keyup="onSearchKeyUp"
      />
    </div>

    <div v-if="filteredOrders.length">
      <template v-for="(order, index) in filteredOrders" :key="order.id ?? index">
        <OrderItem :order="order" :type="props.type" />
      </template>
    </div>

    <div v-else>
      <h5 class="text-center text-primary my-32">
        {{ $t('no-documents') }}
      </h5>
    </div>
  </div>
</template>

<style scoped>
.scanner-capture {
  position: fixed;
  left: -9999px;
  top: -9999px;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
}
</style>
